<template>
  <div data-cy="system-info-card">
    <h3 class="mb-3 text-base font-semibold">{{ $t("systeminfo.title") }}</h3>

    <!-- SPAIN: Declaration link -->
    <p
      @click="openEFRDeclaration"
      v-if="isSpanishCompliant"
      class="link mb-5"
      data-cy="efr-declaration-link"
    >
      DECLARACIÓN RESPONSABLE DEL SISTEMA INFORMÁTICO DE FACTURACIÓN
    </p>

    <!-- FRANCE: NF525 certification info -->
    <div
      v-if="isFrenchCompliant"
      class="logo-info-container"
      data-cy="fr-certification-info"
    >
      <div class="logo-container">
        <img
          src="@/assets/images/icons/nf525.png"
          alt="Logo"
          class="logo-image"
        />
      </div>
      <div class="info-rows">
        <div class="info-row">
          <strong
            >{{
              $t("systeminfo.certificate-number", {}, { locale: "fr" })
            }}:</strong
          >
          {{ ruleForm.certificateNumber }}
        </div>
        <div class="info-row">
          <strong
            >{{
              $t("systeminfo.product-category", {}, { locale: "fr" })
            }}:</strong
          >
          {{ ruleForm.productCategory }}
        </div>
        <div class="info-row">
          <strong
            >{{ $t("systeminfo.pos-name", {}, { locale: "fr" }) }}:</strong
          >
          {{ ruleForm.posName }}
        </div>
        <div class="info-row">
          <strong
            >{{ $t("systeminfo.pos-version", {}, { locale: "fr" }) }}:</strong
          >
          {{ ruleForm.appVersion }}
        </div>
        <div class="info-row">
          <strong>{{ $t("systeminfo.pos-id", {}, { locale: "fr" }) }}:</strong>
          {{ ruleForm.posId }}
        </div>
        <div class="info-row">
          <strong
            >{{ $t("systeminfo.efsta-version", {}, { locale: "fr" }) }}:</strong
          >
          {{ ruleForm.efstaVersion }}
        </div>
        <div class="info-row">
          <button
            class="verify-button"
            data-cy="verify-authenticity-button"
            :disabled="verifyState === 'running'"
            @click="verifyAuthenticity"
          >
            {{ $t("systeminfo.verify-authenticity", {}, { locale: "fr" }) }}
          </button>
        </div>
        <div
          v-if="verifyState !== 'idle'"
          class="verify-status"
          data-cy="verify-authenticity-result"
        >
          <p v-if="verifyState === 'running'">
            {{ $t("systeminfo.verify-running", {}, { locale: "fr" }) }}
          </p>
          <template v-else-if="verifyState === 'ok' && verifyResult">
            <p class="verify-ok">
              ✓
              {{
                $t(
                  "systeminfo.verify-ok",
                  { n: verifyResult.fileCount },
                  { locale: "fr" }
                )
              }}
            </p>
            <p class="verify-hash">
              {{ $t("systeminfo.verify-hash", {}, { locale: "fr" }) }}:
              {{ verifyResult.recomputedGlobalHash }}
            </p>
          </template>
          <template v-else-if="verifyState === 'fail'">
            <p class="verify-fail">
              ✗ {{ $t("systeminfo.verify-fail", {}, { locale: "fr" }) }}
            </p>
            <p v-if="verifyDetail" class="verify-hash">{{ verifyDetail }}</p>
          </template>
        </div>
      </div>
    </div>

    <!-- CROATIA: Fiskalizacija certification info -->
    <div
      v-else-if="isCroatianCompliant"
      class="logo-info-container"
      data-cy="hr-certification-info"
    >
      <div class="info-rows">
        <div class="info-row"><strong>OIB:</strong> {{ ruleForm.oib }}</div>
        <div class="info-row">
          <strong
            >{{ $t("systeminfo.pos-name", {}, { locale: "hr" }) }}:</strong
          >
          {{ ruleForm.posName }}
        </div>
        <div class="info-row">
          <strong>{{ $t("systeminfo.pos-id", {}, { locale: "hr" }) }}:</strong>
          {{ ruleForm.terminalId }}
        </div>
        <div class="info-row">
          <strong
            >{{ $t("systeminfo.pos-version", {}, { locale: "hr" }) }}:</strong
          >
          {{ ruleForm.appVersion }}
        </div>
        <div class="info-row">
          <strong
            >{{ $t("systeminfo.efsta-version", {}, { locale: "hr" }) }}:</strong
          >
          {{ ruleForm.efstaVersion }}
        </div>
      </div>
    </div>

    <!-- DEFAULT: Generic info -->
    <div v-else class="divide-y divide-border">
      <div class="flex items-center justify-between py-2">
        <span class="text-sm text-muted-foreground">
          {{ $t("systeminfo.app-version") }}
        </span>
        <span class="text-sm font-medium">{{ ruleForm.appVersion }}</span>
      </div>
      <div
        v-if="ruleForm.windowsAppVersion && showWindowsAppVersion"
        class="flex items-center justify-between py-2"
      >
        <span class="text-sm text-muted-foreground">
          {{ $t("systeminfo.desktop-app-version") }}
        </span>
        <span class="text-sm font-medium">
          {{ ruleForm.windowsAppVersion }}
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import DeviceModule from "@/store/modules/device";
import UserModule from "@/store/modules/user";
import { defineComponent, reactive, ref } from "vue";
import {
  showWindowsAppVersion,
  isSpanishCompliant,
  isFrenchCompliant,
  isCroatianCompliant,
} from "@/utils/vue-helpers/compliance";
import { EFR_SERVER_URL } from "@/utils/helpers/compliance";
import {
  verifyDeployedRelease,
  type ReleaseVerifyResult,
} from "@/utils/helpers/release-verify";

export default defineComponent({
  name: "SystemInfo",
  setup() {
    const ruleForm = reactive({
      appVersion: import.meta.env.VITE_APP_VERSION,
      windowsAppVersion:
        DeviceModule.hardwareInfo && DeviceModule.hardwareInfo.desktopAppVersion
          ? DeviceModule.hardwareInfo.desktopAppVersion
          : "",
      // France-specific fields
      certificateNumber: import.meta.env.VITE_CERTIFICATE_NUMBER,
      productCategory: import.meta.env.VITE_PRODUCT_CATEGORY,
      posName: import.meta.env.VITE_POS_NAME,
      efstaVersion: import.meta.env.VITE_EFR_VERSION,
      posId: DeviceModule.hardwareLocation?.deviceUid || "",
      // Croatia-specific fields
      oib: UserModule.user.socialSecurityNumber || "",
      terminalId: DeviceModule.hardwareLocation?.deviceId
        ? String(DeviceModule.hardwareLocation.deviceId)
        : "",
    });
    const openEFRDeclaration = () => {
      window.open(`${EFR_SERVER_URL}/declaration`, "_blank");
    };

    // NF525 §4.3.6 (FIS-415): on-demand check that the running application
    // matches its deployed release manifest.
    const verifyState = ref<"idle" | "running" | "ok" | "fail">("idle");
    const verifyResult = ref<ReleaseVerifyResult | null>(null);
    const verifyDetail = ref("");
    const verifyAuthenticity = async () => {
      verifyState.value = "running";
      verifyResult.value = null;
      verifyDetail.value = "";
      try {
        const result = await verifyDeployedRelease();
        verifyResult.value = result;
        verifyState.value = result.ok ? "ok" : "fail";
        if (!result.ok) {
          verifyDetail.value = result.failures.slice(0, 5).join(" · ");
        }
      } catch (error) {
        verifyState.value = "fail";
        verifyDetail.value =
          error instanceof Error ? error.message : String(error);
      }
    };

    return {
      ruleForm,
      showWindowsAppVersion,
      openEFRDeclaration,
      isSpanishCompliant,
      isFrenchCompliant,
      isCroatianCompliant,
      verifyState,
      verifyResult,
      verifyDetail,
      verifyAuthenticity,
    };
  },
});
</script>

<style lang="scss" scoped>
@use "@/styles/variables.scss" as *;
.link {
  text-decoration: underline;
  cursor: pointer;
  color: $color-dark-cyan;
}

.logo-info-container {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 100px;
  margin-bottom: 20px;
}

.logo-container .logo-image {
  width: 100px;
  height: auto;
}

.info-rows {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.info-row {
  font-size: 16px;
}

.verify-button {
  margin-top: 4px;
  padding: 6px 16px;
  font-size: 14px;
  border: 1px solid $color-dark-cyan;
  border-radius: 6px;
  background: transparent;
  color: $color-dark-cyan;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
}

.verify-status {
  max-width: 340px;
  font-size: 13px;
}

.verify-ok {
  color: #2e9e5b;
  font-weight: 600;
}

.verify-fail {
  color: #d33333;
  font-weight: 600;
}

.verify-hash {
  font-family: monospace;
  font-size: 11px;
  word-break: break-all;
  color: #555555;
}
</style>
